import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Bell,
  LayoutList,
  ArrowRight,
  Phone,
  Target,
  Handshake,
  Timer,
} from "lucide-react";
import { Reveal, MaskedLines, MonoLabel } from "../components/Reveal";
import Marquee from "../components/Marquee";
import { IMAGES, PHONE_DISPLAY, PHONE_TEL } from "../data/services";

const pillars = [
  {
    icon: ShieldCheck,
    title: "No account required",
    text: "When time is precious you'll spend none of it creating an account or waiting for prices. Contact us, we verify the details, and a vehicle is dispatched almost immediately.",
  },
  {
    icon: Bell,
    title: "Updates throughout",
    text: "Expedited freight can be stressful — we bring peace of mind with regular updates from pickup through delivery, so you can focus on your normal daily operations.",
  },
  {
    icon: LayoutList,
    title: "Menu pricing",
    text: "Only you know the true importance of a shipment. We provide options matched to your deadline, letting you balance budget and benefits case by case.",
  },
];

const values = [
  {
    icon: Timer,
    title: "Urgency, understood",
    text: "No two urgent freight situations are identical — and neither are the best solutions. We blend speed, accuracy and problem-solving on every shipment.",
  },
  {
    icon: Target,
    title: "Facts over smooth talk",
    text: "We make direct recommendations because we care about your outcome. Many providers tell you what sounds good; we'd rather tell you what will actually work.",
  },
  {
    icon: Handshake,
    title: "Consultative, always",
    text: "We look at what you actually need instead of pushing a generic quote — then break down rate, transit time, risks and pitfalls into clear options.",
  },
];

const AboutPage = () => (
  <main>
    <section data-testid="about-hero" className="relative min-h-[70svh] flex items-end overflow-hidden bg-navy-deep noise-dark">
      <img
        src={IMAGES.truck}
        alt="Way2Go truck on the road at night"
        className="absolute inset-0 h-full w-full object-cover opacity-35"
      />
      <div className="absolute inset-0 bg-navy-deep/50" />
      <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-8 w-full pt-40 pb-14">
        <MonoLabel light className="mb-6">About Way2Go Transport Co.</MonoLabel>
        <MaskedLines
          lines={["WE DELIVER", "SOLUTIONS", "& VALUE."]}
          className="font-display font-black uppercase tracking-tighter leading-[0.92] text-white text-[12vw] sm:text-[8vw] lg:text-[6.5rem]"
        />
        <p className="mt-8 max-w-2xl text-white/70 text-base sm:text-lg leading-relaxed font-medium">
          America's most reliable time-critical freight services — dependable,
          rapid, and always answered by a human.
        </p>
      </div>
    </section>

    <Marquee
      dark
      items={["est. on urgency", "ground · air · nationwide", "yes we do", `call ${PHONE_DISPLAY}`, "no ai · no call centers"]}
    />

    {/* Story */}
    <section data-testid="about-story" className="py-24 sm:py-32 bg-white">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <Reveal className="lg:col-span-7">
          <MonoLabel>Our story</MonoLabel>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-[0.95] text-navy">
            Built for the toughest deadlines
          </h2>
          <div className="mt-8 space-y-6 text-lg text-navy/70 leading-relaxed font-medium">
            <p>
              Way2Go specializes in helping clients meet the toughest deadlines
              by providing the best urgent freight services by ground and air
              across the USA. Our years of expertise, along with a deep network
              of agents and carriers, cover every expedited freight project
              imaginable — resources that let you focus on your business knowing
              your freight is in the best possible hands.
            </p>
            <p>
              When you find yourself racing against the clock, you need an
              expedited delivery company you can rely on. This shipment could be
              the most important one for your company this year — everything
              must go well, on time and within budget. We understand these
              challenges, and we provide the solutions.
            </p>
            <p>
              We meet customers in these situations every single day. As experts
              in expedited freight we understand your expectations and the value
              of your shipment. We only succeed when your shipment arrives on
              time — and we do everything possible to make sure that happens.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border-2 border-royal/20" aria-hidden="true" />
              <img
                src={IMAGES.van}
                alt="Way2Go sprinter van on an expedited run"
                className="relative w-full h-auto"
              />
            </div>
            <div className="mt-8 grid grid-cols-2 gap-px bg-blue-900/10 border border-blue-900/10">
              {[
                ["2-HR", "typical pickup"],
                ["24/7", "human dispatch"],
                ["48", "states covered"],
                ["4.9/5", "shipper rating"],
              ].map(([k, v]) => (
                <div key={v} className="bg-white p-6">
                  <p className="font-mono text-2xl font-bold text-royal">{k}</p>
                  <p className="mt-1 font-mono text-[10px] tracking-[0.18em] uppercase text-navy/45">{v}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* Pillars */}
    <section data-testid="about-pillars" className="py-24 sm:py-32 bg-[#F5F8FD]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
        <Reveal className="mb-14">
          <MonoLabel>How we work</MonoLabel>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-[0.95] text-navy">
            Zero friction, full visibility
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-blue-900/10 border border-blue-900/10">
          {pillars.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="group h-full bg-white p-8 sm:p-10 hover:bg-blue-50/60 transition-colors duration-300">
                <p.icon size={28} strokeWidth={1.75} className="text-royal group-hover:text-royal-bright transition-colors duration-300" />
                <h3 className="mt-6 font-display text-2xl font-bold text-navy">{p.title}</h3>
                <p className="mt-3 text-sm text-navy/55 leading-relaxed font-medium">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Values */}
    <section data-testid="about-values" className="py-24 sm:py-32 bg-white">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <Reveal className="lg:col-span-4">
          <MonoLabel>Our approach</MonoLabel>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-[0.95] text-navy">
            Honest freight counsel
          </h2>
          <p className="mt-6 text-navy/60 leading-relaxed font-medium">
            Freight isn't simple, and neither are the decisions behind it. Every
            shipment is different — and that's exactly how we treat it.
          </p>
        </Reveal>
        <div className="lg:col-span-8 space-y-px bg-blue-900/10 border border-blue-900/10">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="group flex flex-col sm:flex-row sm:items-start gap-5 bg-white p-8 hover:bg-blue-50/60 transition-colors duration-300">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-royal/25 text-royal group-hover:bg-royal group-hover:text-white transition-colors duration-300">
                  <v.icon size={20} strokeWidth={1.75} />
                </span>
                <div>
                  <h3 className="font-display text-xl font-bold text-navy">{v.title}</h3>
                  <p className="mt-2 text-sm text-navy/55 leading-relaxed font-medium">{v.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section data-testid="about-cta" className="py-24 bg-navy-deep noise-dark">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-10">
        <Reveal>
          <MonoLabel light>Ready when you are</MonoLabel>
          <h2 className="mt-4 font-display text-4xl sm:text-6xl font-black uppercase tracking-tighter leading-[0.95] text-white">
            Put us to the test.
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="flex flex-wrap gap-4">
          <a
            href={PHONE_TEL}
            data-testid="about-call-button"
            className="flex items-center gap-3 h-14 px-7 bg-volt text-navy-deep font-display text-lg font-bold tracking-wide shadow-[5px_5px_0px_rgba(0,229,255,0.35)] hover:shadow-[2px_2px_0px_rgba(0,229,255,0.35)] hover:translate-x-[3px] hover:translate-y-[3px] transition-[box-shadow,transform] duration-150"
          >
            <Phone size={18} strokeWidth={2.5} /> CALL {PHONE_DISPLAY}
          </a>
          <Link
            to="/quote"
            data-testid="about-quote-button"
            className="flex items-center gap-3 h-14 px-7 border border-white/30 text-white font-display text-lg font-bold tracking-wide hover:bg-white/10 hover:border-white/60 transition-[background-color,border-color] duration-200"
          >
            GET A QUOTE <ArrowRight size={18} />
          </Link>
        </Reveal>
      </div>
    </section>
  </main>
);

export default AboutPage;
